import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import OrderKittingService from 'services/OrderKittingService';
import {
  ADMIN_HANDLES,
  KittingRule,
  money,
  PreviewOrder,
  PreviewResponse,
  PreviewSample,
} from '../types';

const OUTCOME_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  would_kit: 'success',
  skipped: 'warning',
  no_match: 'default',
};

const OUTCOME_LABEL: Record<string, string> = {
  would_kit: 'Would kit',
  skipped: 'Skipped',
  no_match: 'No match',
};

function OrderRow({ order, store }: { order: PreviewOrder; store: string }) {
  const componentSummary = order.componentsRemoved
    ?.map(c => `${c.sku}: ${c.fromQty} → ${c.toQty}`)
    .join('\n');
  return (
    <TableRow sx={order.outcome === 'would_kit' ? {} : { opacity: 0.75 }}>
      <TableCell>
        <Link
          href={`https://admin.shopify.com/store/${ADMIN_HANDLES[store] ?? 'dosaze'}/orders/${order.orderId}`}
          target="_blank"
          rel="noreferrer"
        >
          {order.orderName}
        </Link>
        <Typography variant="caption" color="text.secondary" display="block">
          {dayjs(order.createdAt).format('MMM D, HH:mm')}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={OUTCOME_LABEL[order.outcome] ?? order.outcome}
          size="small"
          color={OUTCOME_COLOR[order.outcome] ?? 'default'}
        />
        {order.blockers.length > 0 && (
          <Tooltip title={`Would not be edited now: ${order.blockers.join(', ')}`}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ cursor: 'help' }}>
              {order.blockers.join(', ')}
            </Typography>
          </Tooltip>
        )}
        {order.reason && (
          <Typography variant="caption" color="text.secondary" display="block">
            {order.reason}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Stack>
          {order.lineItems.map((line, index) => (
            <Typography
              key={`${line.sku}-${index}`}
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: 11 }}
            >
              {line.quantity} × {line.sku}
            </Typography>
          ))}
        </Stack>
      </TableCell>
      <TableCell align="right">
        {order.outcome === 'would_kit' ? (
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{componentSummary}</Box>}>
            <Typography variant="body2" sx={{ cursor: 'help' }}>
              {order.times} kit{(order.times ?? 0) > 1 ? 's' : ''} · {money(order.paidAmount ?? null, store)}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
}

function SampleTable({ sample, store }: { sample: PreviewSample; store: string }) {
  if (!sample.orders.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No orders found for this sample.
      </Typography>
    );
  }
  return (
    <TableContainer sx={{ maxHeight: 420 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Order</TableCell>
            <TableCell>Outcome</TableCell>
            <TableCell>Items</TableCell>
            <TableCell align="right">Kitting</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sample.orders.map(order => (
            <OrderRow key={order.orderId} order={order} store={store} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// "What would this rule actually do?" — runs the rule (saved or unsaved)
// against real orders on the backend using the job's own matcher, so what you
// see here is what the job would do. Read-only: no order is touched and
// nothing is logged.
export default function PreviewDialog({
  open,
  rule,
  store,
  unsaved = false,
  onClose,
}: {
  open: boolean;
  rule: KittingRule | null;
  store: string;
  // true when previewing a draft from the editor: the rule shown here includes
  // edits that are not saved yet.
  unsaved?: boolean;
  onClose: () => void;
}) {
  const service = React.useMemo(() => new OrderKittingService(), []);
  const [sample, setSample] = React.useState<'matching' | 'recent'>('matching');
  const [result, setResult] = React.useState<PreviewResponse | null>(null);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // The caller may rebuild the rule object on every render (the editor previews
  // a live draft), so fetching is keyed on opening/Refresh — never on the
  // rule's identity, which would loop.
  const latest = React.useRef({ rule, store });
  latest.current = { rule, store };

  const run = React.useCallback(async () => {
    const { rule: current, store: currentStore } = latest.current;
    if (!current) return;
    setLoading(true);
    setError(false);
    setResult(null);
    const response = await service.previewRule(currentStore, current);
    if (response) setResult(response);
    else setError(true);
    setLoading(false);
  }, [service]);

  React.useEffect(() => {
    if (!open) return;
    run();
  }, [open, run]);

  const active = result ? result[sample] : null;
  const wouldKit = active?.counts.would_kit ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview {rule ? `“${rule.ruleId}”` : ''}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Runs this rule against real {store} orders with the job&apos;s own
          matcher — nothing is edited and nothing is logged. Orders already
          fulfilled or otherwise blocked are shown with the reason: the rule
          matches, but the job would not edit them now.
        </Typography>
        {unsaved && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Previewing the rule <strong>as currently edited</strong> — these
            changes are not saved yet.
          </Alert>
        )}

        <Tabs value={sample} onChange={(_, value) => setSample(value)} sx={{ mb: 1 }}>
          <Tab
            value="matching"
            label={`Orders with these items (${result?.matching.orders.length ?? 0})`}
          />
          <Tab value="recent" label={`Most recent orders (${result?.recent.orders.length ?? 0})`} />
        </Tabs>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Checking real orders…
            </Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" action={<Button size="small" onClick={run}>Retry</Button>}>
            Failed to preview this rule.
          </Alert>
        )}

        {active && !loading && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip
                size="small"
                color={wouldKit ? 'success' : 'default'}
                label={`${wouldKit} of ${active.orders.length} would kit`}
              />
              {Object.entries(active.counts)
                .filter(([outcome]) => outcome !== 'would_kit')
                .map(([outcome, count]) => (
                  <Chip
                    key={outcome}
                    size="small"
                    variant="outlined"
                    label={`${count} ${OUTCOME_LABEL[outcome] ?? outcome}`}
                  />
                ))}
            </Stack>
            <SampleTable sample={active} store={store} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={run} disabled={loading}>
          Refresh
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
